/**
 * Primitive color ramps — the raw, context-free palette.
 *
 * Each ramp is a frozen 10-step tuple indexed 0 (lightest) → 9 (darkest),
 * mirroring Mantine's 10-shade convention so the semantic→Mantine adapter can map a
 * semantic intent onto a `MantineColorsTuple` with no value translation.
 *
 * The hex values for purple, orange, and green are reused verbatim from the
 * existing `frontend/src/theme.ts` brand ramps so adopting these tokens shifts
 * nothing visually. The red and neutral ramps are authored here for the first
 * time: `theme.ts` flat-fills red with `#b63333` ×10 (a latent bug — a single
 * shade cannot express hover/active/disabled states), and has no neutral ramp
 * at all.
 */
/**
 * Brand primary. Shade 7 (`#470047`) is the canonical brand purple
 * (`theme.primaryColor`).
 */
export const purple = [
    '#f9e6f9',
    '#f2ccf2',
    '#eb99eb',
    '#e466e4',
    '#cc33cc',
    '#b300b3',
    '#800080',
    '#470047',
    '#330033',
    '#1a001a',
];
/** Brand accent. Shade 6 (`#ffb94e`) is the canonical accent orange. */
export const orange = [
    '#fff8eb',
    '#ffecd6',
    '#ffe0c2',
    '#ffd4ad',
    '#ffc799',
    '#ffbb85',
    '#ffb94e',
    '#ff8c1a',
    '#e67300',
    '#cc6600',
];
/**
 * Warning / caution. A dedicated functional-amber ramp, distinct from the brand
 * `orange` accent (which stays for links). Amber is the highest-luminance hue, so
 * white text never reaches AA on it — filled warning pairs the brand amber (shade 3,
 * `#feb84e`) with purple ink, and text/borders on white use the dark shades (7–9).
 */
export const amber = [
    '#fef6e6',
    '#fdefcb',
    '#f2d28a',
    '#feb84e',
    '#f5ab39',
    '#ec9f28',
    '#c77a15',
    '#b45309',
    '#7a3e06',
    '#6b3a07',
];
/** Success / positive. Shade 6 (`#607d12`) is the anchor used by `theme.ts`. */
export const green = [
    '#e8f0c8',
    '#d4e4a0',
    '#bdd878',
    '#a6cb52',
    '#8fb82e',
    '#75a118',
    '#607d12',
    '#4a6109',
    '#344603',
    '#1e2b00',
];
/**
 * Danger / destructive. A real 10-step ramp anchored on the existing brand red
 * `#b63333` at shade 6, gradated light → dark. Replaces the flat `#b63333` ×10
 * fill in `theme.ts`, which could not express interactive state.
 */
export const red = [
    '#fbeaea',
    '#f4caca',
    '#e9a3a3',
    '#dd7d7d',
    '#d05858',
    '#c34343',
    '#b63333',
    '#922929',
    '#6d1f1f',
    '#491414',
];
/**
 * Informational. A dedicated blue ramp so `info` carries its own hue instead of
 * sharing brand purple. Shade 6 (`#4675be`) is the anchor — white text on it is
 * 4.62:1, just past AA, so the anchor must never lighten without flipping
 * on-info text to ink. Registered in the Mantine adapter, so a bare
 * `color="blue"` resolves here rather than to Mantine's built-in azure.
 */
export const blue = [
    '#eef4fe',
    '#d1dff5',
    '#b0c7eb',
    '#8da9d5',
    '#6b8dc1',
    '#5881c0',
    '#4675be',
    '#335b9b',
    '#234374',
    '#162c4f',
];
/**
 * Neutral / gray. A standard 10-step neutral ramp (new — `theme.ts` has none),
 * used for surfaces, borders, and muted text in the semantic layer.
 */
export const neutral = [
    '#f8f9fa',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#868e96',
    '#495057',
    '#343a40',
    '#212529',
];
/**
 * All primitive ramps keyed by name. Consumers resolve a semantic intent's
 * `{ ramp, shade }` against this map: `primitiveRamps[ramp][shade]`.
 */
export const primitiveRamps = {
    purple,
    orange,
    amber,
    blue,
    green,
    red,
    neutral,
};
/**
 * Standalone primitive colors that are not part of a 10-step ramp. Kept
 * separate so the ramp tuples stay uniform. Semantic tokens point at these for
 * pure surfaces/inverses.
 *
 * `ink` is TEO's darkest brand foreground, the literal-layer name for what reads
 * as "default dark text". It is *derived from* `purple[7]` (not a hardcoded
 * copy) so a future rebrand/a11y change to the purple ramp carries through
 * automatically and the two cannot silently drift. It is deliberately NOT named
 * `black`: this is a framework-agnostic primitive whose names also ship into
 * W3C/Style-Dictionary/Tailwind exports, where `black` is expected to be
 * `#000000`. The Mantine adapter maps `ink` into Mantine's reserved
 * `theme.black` slot (so default text renders brand purple) — that framework
 * coupling lives in the adapter, not here.
 */
export const commonColors = {
    white: '#ffffff',
    ink: purple[7],
    transparent: 'transparent',
};
